// Launch round 1 (Lane 1440, Ryan's mockup v1): a short confirmation in place of the long
// Review and publish panel. It first waits for every save the buyer made by leaving a
// field (a failed one stops it), then reads the Overview once and shows those values; the
// launch sends exactly them, with that read's revision, to the existing publish action
// (same checks, receipts, notices and emails). Anything changed after the read is refused
// as stale and the dialog reads again.
import React, { useCallback, useEffect, useState } from 'react';
import { STALE_MESSAGE, endSentence } from './sourcingApi';
import InvitationLinks, { DELIVERY_TEXT } from './InvitationLinks';
import { CheckList } from './PublishReview';
import { offersDueLocal, offersDueText } from './RfpSetupCard';
import { viewerZone } from './useSourcingChat';
import useDialogFocus from './useDialogFocus';
import { saveBlob } from './DocumentsTab';
import { EventHeader, EventDetails, PartsTable, formatDueAt } from '../sourcing-portal/portalView';

// The deadline Launch sends: the saved instant, else the saved day at 5 PM here.
function launchDue(setup) {
  const local = offersDueLocal(setup && setup.offers_due);
  const d = local ? new Date(local) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

function names(list) {
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

const MISSING_TEXT = {
  offers_due: 'Set when offers are due (Overview, RFP setup).',
  offers_due_passed: 'Offers due has passed or is less than 5 minutes away. Choose a later time.',
  no_invitees: 'Every supplier declined. Add a supplier to invite.',
};

function checks(launch, setup, emailEnabled) {
  const out = [];
  const n = launch.of;
  if (launch.no_email.length) {
    out.push(['block', `${names(launch.no_email)} ${launch.no_email.length === 1 ? 'has' : 'have'} no valid email yet.`]);
  } else if (emailEnabled) {
    out.push(['ok', `${n === 1 ? 'The supplier gets' : `All ${n} suppliers get`} the Wingman email with their login and bid steps.`]);
  } else {
    out.push(['warn', 'Email is off. After you launch, you copy each supplier\'s link and send it yourself.']);
  }
  if (launch.email_conflicts.length) {
    out.push(['block', `${names(launch.email_conflicts)}: the email already works with you for another supplier.`]);
  }
  out.push(setup.offers_due && setup.offers_due.due_date
    ? ['ok', `Offers due ${offersDueText(setup.offers_due)}`] : ['block', 'Offers due is not set.']);
  out.push(['ok', `${plural(launch.parts, 'part')} at ${plural(launch.sites, 'site')}`]);
  if (launch.confirmed === n) out.push(['ok', n === 1 ? 'The supplier confirmed.' : `Every supplier confirmed (${n}).`]);
  else out.push(['warn', `${launch.confirmed} of ${n} confirmed. The others still get the email.`]);
  if (launch.declined.length) out.push(['warn', `${names(launch.declined)} declined and ${launch.declined.length === 1 ? 'is' : 'are'} not invited.`]);
  if (launch.title_flag) {
    const named = (launch.title_names || []).length ? ` (${launch.title_names.join(', ')})` : '';
    out.push(['warn', `The title names one of your suppliers${named}. Every supplier will see it.`]);
  }
  launch.missing.filter((m) => m.message || MISSING_TEXT[m.code]).forEach((m) => {
    if (m.code !== 'offers_due') out.push(['block', m.message || MISSING_TEXT[m.code]]);
  });
  return out;
}

// What each supplier gets at launch (Lane 1440, Ryan): the invitation email exactly as
// publish builds it (only the personal link differs per supplier), and the bid guide.
function WhatSuppliersReceive({ api, eventId, mail, emailOn }) {
  const [error, setError] = useState('');
  const guide = () => api.bidGuide(eventId)
    .then((b) => saveBlob(b, 'How to submit your bid.pdf')).catch((e) => setError(e.message));
  if (!mail) return null;
  return (
    <section className="srce-inv-col" aria-label="What suppliers receive" data-testid="srce-launch-email">
      <span className="srce-strong">What suppliers receive</span>
      <span className="srce-mut">{endSentence(`${emailOn ? 'This email from Wingman'
        : 'Email is off: you send each supplier their link and this text yourself'}${mail.to ? `, shown for ${mail.to.supplier_name}` : ''}`)}</span>
      <pre className="srce-guide-email">{`Subject: ${mail.subject}\n\n${mail.body}`}</pre>
      <span className="srce-row">
        {(mail.attachments || []).length > 0 && <span className="srce-mut">Attached: {mail.attachments.join(', ')}</span>}
        <button type="button" className="ll-clr" onClick={guide}>Bid guide (PDF)</button>
      </span>
      {error && <span className="srce-error" role="alert">{error}</span>}
    </section>
  );
}

function Preview({ api, eventId, title, shareSites, onBack }) {
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    api.publishReview(eventId, { supplier_title: title, share_sites: shareSites, preview_page: page })
      .then((r) => { if (live) setPreview(r.preview); }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [api, eventId, title, shareSites, page]);
  return (
    <div className="srce-inv-col">
      <span className="srce-row"><button type="button" className="ll-clr" onClick={onBack}>Back</button>
        <span className="srce-strong">What suppliers see</span><span className="srce-mut">Read only</span></span>
      {error && <div className="srce-error" role="alert">{error}</div>}
      {!preview && !error && <div className="ll-loading">Loading…</div>}
      {preview && (
        <div className="srce-preview-box">
          <EventHeader untitled="No title yet" event={{ ...preview.event, round: preview.event.round || 1 }} />
          <EventDetails event={preview.event} />
          {preview.parts && preview.parts.items.length > 0 && (
            <>
              <div className="srce-mut srce-gap srce-row">
                Page {preview.parts.page} of {preview.parts.total_pages} ({preview.parts.total.toLocaleString()} parts).
                <button type="button" className="srce-btn" disabled={preview.parts.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
                <button type="button" className="srce-btn" disabled={preview.parts.page >= preview.parts.total_pages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
              <PartsTable items={preview.parts.items} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function LaunchDialog({ api, eventId, saveQueue, onClose, onLaunched, onGoInvitations }) {
  const [snap, setSnap] = useState(null);           // {revision, setup, launch, email}
  const [error, setError] = useState('');
  const [blocks, setBlocks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareSites, setShareSites] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  const box = useDialogFocus(busy ? () => {} : onClose);

  const read = useCallback(async () => {
    setSnap(null);
    setError('');
    // Every save the buyer made first; a failed one stops here (Codex plan r1 #3).
    if (!(await saveQueue.flush())) {
      setError(`Some details are not saved: ${saveQueue.error || 'check your connection'}. Fix them, then launch.`);
      return;
    }
    try {
      const [ov, review] = await Promise.all([api.overview(eventId), api.invitationLetter(eventId)]);
      if (!ov.invitations || !ov.invitations.launch) { setError('This event is not a draft any more.'); return; }
      // The email with the deadline and zone this dialog will send (one rule, launchDue).
      const due = launchDue(ov.invitations.setup);
      const zone = viewerZone();
      const email = await api.invitationEmailPreview(eventId, {
        ...(due ? { due_at: due.toISOString() } : {}), ...(zone ? { time_zone: zone } : {}) });
      setSnap({ revision: ov.revision, setup: ov.invitations.setup, launch: ov.invitations.launch,
        email: review.email_enabled, letterNames: review.names_supplier || [], mail: email });
      setShareSites(Boolean(ov.invitations.launch.share_sites));
    } catch (e) {
      setError(e.message);
    }
  }, [api, eventId, saveQueue]);
  useEffect(() => { read(); }, [read]);

  const launch = async () => {
    const { setup, launch: l, revision } = snap;
    const due = launchDue(setup);
    setBusy(true);
    setError('');
    setBlocks(null);
    try {
      const zone = viewerZone();
      const r = await api.publish(eventId, {
        supplier_title: setup.title, due_at: due.toISOString(), share_sites: shareSites,
        ...(zone ? { time_zone: zone } : {}),
        contacts: l.invitees.map((s) => ({ supplier_id: s.supplier_id, email: s.email.trim(),
          ...(s.name && s.name.trim() ? { name: s.name.trim() } : {}) })),
      }, revision);
      setResult({ ...r, names: Object.fromEntries(l.invitees.map((s) => [s.supplier_id, s.supplier_name])) });
      await onLaunched();
    } catch (e) {
      if (e.code === 'publish_blocked') {
        setBlocks((e.detail && e.detail.blocks) || []);
        setError(e.message);
      } else if (e.code === 'stale_revision') {
        setError(STALE_MESSAGE);
        await read();
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (result) {
    const inv = (result.result && result.result.invitations) || [];
    const delivery = result.delivery || {};
    const due = formatDueAt(result.result && result.result.due_at);
    body = (
      <>
        <div className="srce-good">Round 1 is open{due ? ` until ${due}` : ''}. The parts in this RFP can no longer change.</div>
        {result.links && result.links.length > 0 ? <InvitationLinks links={result.links} names={result.names} /> : (
          <ul className="srce-plainlist">
            {inv.map((i) => (
              <li key={i.invitation_id}><span className="srce-strong">{i.supplier_name}</span>, {i.email}:{' '}
                {DELIVERY_TEXT[delivery[i.invitation_id] || i.delivery] || 'Invitation saved'}</li>
            ))}
          </ul>
        )}
      </>
    );
  } else if (previewing && snap) {
    body = <Preview api={api} eventId={eventId} title={snap.setup.title} shareSites={shareSites} onBack={() => setPreviewing(false)} />;
  } else if (!snap) {
    body = error ? null : <div className="ll-loading">Checking the RFP…</div>;
  } else {
    const items = checks(snap.launch, snap.setup, snap.email);
    // What Review and publish showed under "Check these" (Codex code r1 #2), and a letter
    // that names a supplier (the letter goes out before launch, so it is only a warning).
    (snap.launch.warnings || []).forEach((x) => items.push(['warn', x.message]));
    if (snap.letterNames.length) {
      items.push(['warn', `The RFP invitation letter names one of your suppliers (${snap.letterNames.join(', ')}).`]);
    }
    body = (
      <>
        <div className="srce-mut">&ldquo;{snap.setup.title}&rdquo;</div>
        <ul className="srce-launch-list">
          {items.map(([tone, text], i) => (
            <li key={i} className={`srce-launch-i srce-launch-i--${tone}`}>
              <span className="srce-launch-ic" aria-hidden="true">{tone === 'ok' ? '✓' : '!'}</span>
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <WhatSuppliersReceive api={api} eventId={eventId} mail={snap.mail} emailOn={snap.email} />
        <label className="srce-inline">
          <input type="checkbox" checked={shareSites} disabled={busy} onChange={(e) => setShareSites(e.target.checked)} />
          Share site names with suppliers
        </label>
        {blocks && blocks.length > 0 && <CheckList items={blocks} tone="block" />}
      </>
    );
  }
  const ready = snap && snap.launch.ready && !busy;
  return (
    <div className="srce-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className={`srce-modal ${previewing ? '' : 'srce-modal--narrow'}`} role="dialog" aria-modal="true"
        aria-label={result ? 'Round 1 launched' : 'Launch round 1'} ref={box}>
        <div className="srce-card2-h"><span className="srce-card2-t">{result ? 'Round 1 launched' : 'Launch round 1?'}</span></div>
        <div className="srce-modal-b srce-launch-b">
          {error && <div className="srce-error" role="alert">{error}</div>}
          {body}
        </div>
        <div className="srce-modal-f">
          {result ? (
            <span className="srce-push srce-row">
              <button type="button" className="srce-btn" onClick={() => onGoInvitations()}>Go to invitations</button>
              <button type="button" className="srce-btn srce-btn--primary" onClick={onClose}>Close</button>
            </span>
          ) : (
            <>
              {snap && !previewing && <button type="button" className="ll-clr" onClick={() => setPreviewing(true)}>Preview what suppliers see</button>}
              <span className="srce-push srce-row">
                <button type="button" className="srce-btn" disabled={busy} onClick={onClose}>Not yet</button>
                <button type="button" className="srce-btn srce-btn--primary" disabled={!ready} onClick={launch}>
                  {busy ? 'Launching…' : 'Launch round 1'}</button>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
